import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import {
  User,
  Bell,
  Shield,
  Server,
  Check,
  X
} from 'lucide-react'

export function Settings() {
  const { user } = useAuth()

  const { data: features } = useQuery({
    queryKey: ['features'],
    queryFn: () => api.getFeatures(),
  })

  const { data: health } = useQuery({
    queryKey: ['health'],
    queryFn: () => api.getHealth(),
  })

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Profile */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <User className="w-5 h-5 text-slate-600" />
          <h3 className="text-lg font-semibold text-slate-900">Profile</h3>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-2xl font-bold text-primary-700">
                {user?.name?.charAt(0) || 'U'}
              </span>
            </div>
            <div>
              <p className="font-medium text-slate-900">{user?.name || 'User'}</p>
              <p className="text-sm text-slate-500">{user?.email || 'user@example.com'}</p>
              <span className="badge-info mt-1 capitalize">{user?.role || 'User'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <Bell className="w-5 h-5 text-slate-600" />
          <h3 className="text-lg font-semibold text-slate-900">Notifications</h3>
        </div>

        <div className="space-y-4">
          <ToggleSetting
            label="Signal Notifications"
            description="Receive content alerts via Signal Messenger"
            enabled={features?.features.signalIntegration}
            disabled
          />
          <ToggleSetting
            label="AI Content Alerts"
            description="Get notified when new AI content is ready for review"
            enabled={true}
            disabled
          />
          <ToggleSetting
            label="SEO Opportunities"
            description="Alerts for new ranking opportunities"
            enabled={true}
            disabled
          />
        </div>
      </div>

      {/* System Status */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <Server className="w-5 h-5 text-slate-600" />
          <h3 className="text-lg font-semibold text-slate-900">System Status</h3>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between py-2">
            <span className="text-slate-700">API Status</span>
            <div className="flex items-center gap-2">
              {health?.status === 'healthy' ? (
                <>
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  <span className="text-green-600 text-sm">Healthy</span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 bg-red-500 rounded-full" />
                  <span className="text-red-600 text-sm">Error</span>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between py-2">
            <span className="text-slate-700">Version</span>
            <span className="text-slate-500 text-sm">{features?.version || 'Unknown'}</span>
          </div>

          <div className="flex items-center justify-between py-2">
            <span className="text-slate-700">Environment</span>
            <span className="text-slate-500 text-sm capitalize">{health?.environment || 'Unknown'}</span>
          </div>
        </div>
      </div>

      {/* Feature Flags */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-5 h-5 text-slate-600" />
          <h3 className="text-lg font-semibold text-slate-900">Feature Status</h3>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FeatureItem
            label="Content Management"
            enabled={features?.features.contentManagement}
          />
          <FeatureItem
            label="AI Generation"
            enabled={features?.features.aiContentGeneration}
          />
          <FeatureItem
            label="Signal Bot"
            enabled={features?.features.signalIntegration}
          />
          <FeatureItem
            label="Multi-platform"
            enabled={Object.values(features?.features.platforms || {}).some(Boolean)}
          />
          <FeatureItem
            label="SEO Intelligence"
            enabled={Object.values(features?.features.seo || {}).some(Boolean)}
          />
          <FeatureItem
            label="AI Available"
            enabled={features?.features.ai.available}
          />
        </div>
      </div>
    </div>
  )
}

function ToggleSetting({
  label,
  description,
  enabled,
  disabled,
}: {
  label: string
  description: string
  enabled?: boolean
  disabled?: boolean
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="font-medium text-slate-900">{label}</p>
        <p className="text-sm text-slate-500">{description}</p>
      </div>
      <button
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          enabled ? 'bg-primary-600' : 'bg-slate-200'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  )
}

function FeatureItem({
  label,
  enabled
}: {
  label: string
  enabled?: boolean
}) {
  return (
    <div className={`flex items-center gap-2 p-3 rounded-lg ${
      enabled ? 'bg-green-50' : 'bg-slate-50'
    }`}>
      {enabled ? (
        <Check className="w-4 h-4 text-green-600" />
      ) : (
        <X className="w-4 h-4 text-slate-400" />
      )}
      <span className={enabled ? 'text-green-900' : 'text-slate-500'}>
        {label}
      </span>
    </div>
  )
}
